<template>
  <div class="settlement-payments-table table-component p-2">

    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :checkboxes="true" :search="tableData.search"
                   :enable-simple-search="false" export-vuex-module-name="settlementPayments" enable-frontend-export
                   export-permission-name="export_settlement"
                   @paramsChanged="getTableData" selectable filter-toggle-string="settlement-payments-table"
                   show-toggle-columns :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns">

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" class="mr-2"
                    v-permission="'change_settlement'">
          <b-dropdown-item @click="onGenerateBatchPaymentsClick(selectedItems)">
            {{ $t('Generate Batch Payments') }}
          </b-dropdown-item>
        </b-dropdown>

      </template>

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="settlement-payments-table">
          <settlement-payment-search/>
        </search-collapse>
      </template>

      <template slot="cell(settlement_no)" slot-scope="{item: settlementPayment}">
        <a href="javascript:void(0)" @click="onViewSettlementPdfClick(settlementPayment)">
          {{ settlementPayment.settlement.settlement_no }}
        </a>
        <action-buttons :delete-permission="'delete_settlement'"
                        :edit-permission="'change_settlement'"
                        :view="false"
                        :edit="!(['debited', 'cancelled'].includes(settlementPayment.settlement.status) || settlementPayment.check_uuid || settlementPayment.financial_export || settlementPayment.batch_id)"
                        :remove="!(['debited', 'cancelled'].includes(settlementPayment.settlement.status) || settlementPayment.check_status === 'cancelled' || settlementPayment.financial_export || settlementPayment.batch_id)"
                        @editClick="onEditClick(settlementPayment)"
                        @deleteClick="onRemoveClick(settlementPayment)"/>
      </template>

      <template slot="cell(settlement_type)" slot-scope="{item: settlementPayment}">
        <span class="badge border">
          {{ settlementPayment.settlement.type | removeUnderscores | toUpperCaseFirst | translate}}
        </span>
      </template>

      <template slot="cell(sale_no)" slot-scope="{item: settlementPayment}">
        <router-link v-if="settlementPayment.settlement.auction && !enableContinuousAuction"
                     v-permission="'view_auction'"
                     :to="{ name: 'sales.view', params: { id: settlementPayment.settlement.auction.uuid }}">
          {{ settlementPayment.settlement.auction.sale_no }}
        </router-link>
        <span v-else-if="settlementPayment.settlement.auction && enableContinuousAuction">
          {{ settlementPayment.settlement.auction.sale_no }}
        </span>
      </template>

      <template slot="cell(customer)" slot-scope="{item: settlementPayment}">
        <template v-if="settlementPayment.settlement.customer" v-permission="'view_customer'">
          <router-link :to="{ name: 'customers.dashboard', params: { id: settlementPayment.settlement.customer.uuid }}">
            {{ settlementPayment.settlement.customer.display_name }}
          </router-link>
        </template>
      </template>

      <template slot="cell(company_name)" slot-scope="{item: settlementPayment}">
        <template v-if="settlementPayment.settlement.customer">
          {{ settlementPayment.settlement.customer.company_name }}
        </template>
      </template>

      <template slot="cell(customer_no)" slot-scope="{item: settlementPayment}">
        <template v-if="settlementPayment.settlement.customer" v-permission="'view_customer'">
          <router-link :to="{ name: 'customers.dashboard', params: { id: settlementPayment.settlement.customer.uuid }}">
            {{ settlementPayment.settlement.customer.customer_no }}
          </router-link>
        </template>
      </template>

      <template slot="cell(payment_type)" slot-scope="{item: settlementPayment}">
        <span class="badge border">
          {{ settlementPayment.payment.type | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
      </template>

      <template slot="cell(method)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.method | removeUnderscores | toUpperCaseFirst | translate }}
      </template>

      <template slot="cell(amount)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.amount | asCurrency(settlementPayment.payment.currency.symbol) }}
      </template>

      <template slot="cell(date)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.date | toDate }}
      </template>

      <template slot="cell(bank_account)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.bank_account }}
      </template>

      <template slot="cell(reference_number)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.reference_number }}
      </template>

      <template slot="cell(description)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.description }}
      </template>

      <template slot="cell(comment)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.comment }}
      </template>

      <template slot="cell(batch_info)" slot-scope="{item: settlementPayment}">
        <div v-if="settlementPayment.payment.batch_payment">
          <div>
            <a href="javascript:void(0)"
               @click="onBatchEditClick(settlementPayment)">{{ settlementPayment.payment.batch_payment.payment_no }}</a>
          </div>
          <span v-as-status="settlementPayment.payment.batch_payment.status">
            {{ settlementPayment.payment.batch_payment.status | removeUnderscores | toUpperCaseFirst | translate }}
          </span>
        </div>
      </template>

      <template slot="cell(created_at)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.created_at | toDate }} by {{ settlementPayment.payment.created_by.display_name }}
      </template>

      <template slot="cell(updated_at)" slot-scope="{item: settlementPayment}">
        {{ settlementPayment.payment.updated_at | toDate }} by {{ settlementPayment.payment.updated_by.display_name }}
      </template>

      <template slot="cell(reconciled)" slot-scope="{item: settlementPayment}">
        <span class="badge bg-success text-white" v-if="settlementPayment.payment.reconciled">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>

      <template slot="cell(check_status)" slot-scope="{item: settlementPayment}">
        <span v-if="settlementPayment.check_status" v-as-status="settlementPayment.check_status">
          {{ settlementPayment.check_status | removeUnderscores | toUpperCaseFirst }}
        </span>
      </template>

      <template slot="cell(beneficiary_relation)" slot-scope="{item: settlementPayment}">

        <template v-if="settlementPayment.payment.beneficiary_relation">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{ name: 'customers.dashboard', params: { id: settlementPayment.payment.beneficiary_relation.related_customer.uuid }}">
            {{ settlementPayment.payment.beneficiary_relation.related_customer.display_name }}
          </router-link>
          <div v-else>
            {{ settlementPayment.payment.beneficiary_relation.related_customer.display_name }}
          </div>
        </template>

      </template>


      <template slot="cell(starting_check_number)" slot-scope="{item: settlementPayment}">
        <span
          v-if="settlementPayment.payment.settlement_check && settlementPayment.payment.settlement_check.length > 0">
          {{ settlementPayment.payment.settlement_check[0].starting_check_number }}
        </span>
      </template>

      <template slot="cell(starting_check_number)" slot-scope="{item: settlementPayment}">
        <span
          v-if="settlementPayment.payment.settlement_check && settlementPayment.payment.settlement_check.length > 0">
          {{ settlementPayment.payment.settlement_check[0].starting_check_number }}
        </span>
      </template>

      <template slot="cell(payment_history)" slot-scope="{item: settlementPayment}">
        <exports :item="{settlement_payments: [settlementPayment]}" current-field="settlement_payments"
                 :on-show-more-click="onShowPaymentMoreClick"/>
      </template>

      <template slot="cell(vendor_group_account)" slot-scope="{item: settlementPayment}" v-if="vendorBuyerGroupAccountsEnabled">
        <template v-if="settlementPayment.settlement.auction">
          {{ settlementPayment.settlement.auction.vendor_group_account }}
        </template>
      </template>

      <template slot="cell(exchange_rate)" slot-scope="{item: settlementPayment}">
        <template v-if="settlementPayment.payment">
          {{ settlementPayment.payment.exchange_rate }}
        </template>
      </template>

      <template slot="cell(branch)" slot-scope="{item: settlementPayment}">
        <template v-if="settlementPayment.settlement.branch">
          {{ settlementPayment.settlement.branch.name }}
        </template>
      </template>

      <template slot="cell(actions)" slot-scope="{item: settlementPayment}">
        <b-dropdown right class="text-white" variant="primary" no-caret size="sm"
                    v-if="(settlementPayment.payment.settlement_check && settlementPayment.payment.settlement_check.length > 0)
                    || (settlementPayment.payment.settlement_bacs && settlementPayment.payment.settlement_bacs.length > 0)
                     || !['debited', 'cancelled'].includes(settlementPayment.settlement.status) ">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v" class="icon"/>
          </template>

          <b-dd-item @click="viewCheckClick(settlementPayment, false)"
                     v-if="settlementPayment.payment.settlement_check && settlementPayment.payment.settlement_check.length > 0">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('View Single Check') }}
          </b-dd-item>

          <b-dd-item @click="viewCheckClick(settlementPayment, true)"
                     v-if="settlementPayment.payment.settlement_check && settlementPayment.payment.settlement_check.length > 0">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('View Batch Check') }}
          </b-dd-item>

          <b-dd-item @click="onBacsCsvDownloadClick(settlementPayment, true)"
                     v-if="settlementPayment.payment.settlement_bacs && settlementPayment.payment.settlement_bacs.length > 0">
            <font-awesome-icon icon="file-invoice" style="font-size: .9em" class="mr-1"/>
            {{ $t('Download BACS CSV') }}
          </b-dd-item>

          <refund-payment-d-d-item v-if="!['debited', 'cancelled'].includes(settlementPayment.settlement.status)"
                                   :settlement-payment="settlementPayment" @success="onRefundSuccess"/>

        </b-dropdown>

      </template>

    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import cloneDeep from "lodash/cloneDeep";
  import SettlementPaymentSearch from "@/modules/settlement-payments/SettlementPaymentSearch";
  import {
    SETTLEMENT_PAYMENTS_TABLE_NAME,
    SOURCE_SETTLEMENT_PAYMENT_EXPORTS,
    SOURCE_SETTLEMENT_PAYMENTS_TABLE
  } from "@/modules/constants";
  import SettlementService from "@/modules/settlements/SettlementService";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import BacsService from "@/modules/bacs-payments/BacsService";
  import RefundPaymentDDItem from "@/modules/settlement-payments/RefundPaymentDDItem";
  import Exports from "@/core/components/exports/Exports";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState, mapActions} = createNamespacedHelpers('settlementPayments');
  const {mapActions: settlementsMapActions} = createNamespacedHelpers('settlements');

  export default {
    name: "SettlementPaymentTable",
    components: {Exports, RefundPaymentDDItem, SettlementPaymentSearch, SearchCollapse, ActionButtons, ArtisioTable},
    data() {
      return {
        selectedItems: new Set(),
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
      }),
      ...mapStateGlobal({
        enableContinuousAuction: state => state.enableContinuousAuction,
        vendorBuyerGroupAccountsEnabled: state => state.vendorBuyerGroupAccountsEnabled,
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f =>
          f.isVisible && (this.vendorBuyerGroupAccountsEnabled || f.key !== 'vendor_group_account')
        );
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.vendorBuyerGroupAccountsEnabled ? [] : ['vendor_group_account'],
          tableName: `${SETTLEMENT_PAYMENTS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'updateToggleColumns']),
      ...settlementsMapActions([
        'showSettlementBatchPaymentModal',
        'getSettlementBatchPaymentModalObject',
        'showPaymentModal',
        'updatePaymentModalObject',
        'showPaymentExportsModal'
      ]),

      async onEditClick(settlementPayment) {
        await SettlementService.onEditPayment(this, settlementPayment, SOURCE_SETTLEMENT_PAYMENTS_TABLE, settlementPayment.settlement)
      },
      async onRemoveClick(settlementPayment) {
        await SettlementService.onDeletePayment(this, settlementPayment, SOURCE_SETTLEMENT_PAYMENTS_TABLE)
      },
      async onGenerateBatchPaymentsClick() {
        this.showSettlementBatchPaymentModal({
          source: SOURCE_SETTLEMENT_PAYMENTS_TABLE
        })
      },
      async onBatchEditClick(settlementPayment) {
        this.showSettlementBatchPaymentModal({
          source: SOURCE_SETTLEMENT_PAYMENTS_TABLE
        })
        this.getSettlementBatchPaymentModalObject(settlementPayment.payment.batch_payment.uuid)
      },
      onViewSettlementPdfClick(settlementPayment) {
        SettlementService.viewSettlementPdf(this, settlementPayment.settlement)
      },
      async viewCheckClick(settlementPayment, merged) {
        let item = settlementPayment.payment.settlement_check[0]

        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: merged ? this.$i18n.t('Batch Check') : this.$i18n.t('Check {check_no}', {check_no: item.check_number})
        });
        let response = await SettlementService.getSettlementCheckPdfData(item.uuid, merged);
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: response.body.pdf_url,
            filename: 'Check'
          })
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
          let errorMessage = "";
          if (response.body.non_field_errors) {
            errorMessage = response.body.non_field_errors[0];
          } else {
            errorMessage = response.body;
          }
          this.$errorDialog(errorMessage);
        }
      },
      async onBacsCsvDownloadClick(settlementPayment) {
        let item = settlementPayment.payment.settlement_bacs[0]
        let response = await BacsService.getSettlementBacsCsvData(item.uuid);
        if (response.success) {
          window.open(response.body.csv_url, '_blank');
        }
      },
      onRefundSuccess() {
        this.getTableData()
      },
      onShowPaymentMoreClick(item) {
        this.showPaymentExportsModal({
          settlement: item,
          source: SOURCE_SETTLEMENT_PAYMENT_EXPORTS
        })
      },
    },
    mounted() {
      this.getTableData();
    },
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {
    }
  }
</style>

